/* eslint-disable */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import produce from 'immer';
import { LoadedEditorResources } from '@/features/editor/use-editor-loader';
import {
  ExperienceStateHook,
  useExperienceState,
} from '@/features/editor/context/use-experience-state';
import {
  TranspilerHook,
  useTranspiler,
} from '@/features/editor/context/use-transpiler';
import {
  AiVisualEditMessage,
  BaseMessage,
  CheckoutNewComponent,
  CopyElementMessage,
  DevicePreviewHook,
  ElementMessage,
  ExtensionType,
  ExternalEditMessage,
  LightweightMountPoint,
  LoadedMessage,
  MoveElementMessage,
  NavigateToMessage,
  NewElementMessage,
  OpenLinkMessage,
  PageLoadErrorMessage,
  SectionModifierAddMessage,
  useDevicePreview,
} from '@/features/editor/context/use-device-preview';
import {
  InspectorNavigationHook,
  useInspectorNavigation,
} from '@/features/editor/context/use-inspector-navigation';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
  ElementTracking,
  Experience,
  HtmlTransformKind,
  PlacementKind,
  TargetingDeviceType,
  toTargetingDeviceType,
} from '@/webapi/use-experience-api';
import {
  MINI_CART_TAG,
  useDeviceNavigation,
  UseDeviceNavigationHook,
} from '@/features/editor/context/use-device-navigation';
import {
  UseExperienceControls,
  useExperienceControls,
} from '@/features/editor/context/use-experience-controls';
import { CustomWidgetStep } from '@/features/editor/widgets/custom-widget/shared/context';
import { shouldSync } from '@/utils/experience-utils';
import { DeviceType } from '@/utils/definitions';
import { AccountContext, useFeatureBit } from '@/features/account-context';
import { useWidgetCatalogApi } from '@/webapi/use-widget-catalog-api';
import {
  EDITOR_EXPERIENCE_CACHE,
  EDITOR_UNSAVED_EXPERIENCE_CACHE,
  useCachedAutoSave,
} from '@/components/hooks/use-cached-auto-save';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import {
  ORIGINAL_EXP_ID,
  ORIGINAL_EXP_NAME,
} from '@/features/editor/shared/external_experience_widget';
import { WidgetChange } from '@/pkg/sdk';
import {
  Env,
  routes,
  SHOPIFY_POST_PURCHASE_PROD,
  SHOPIFY_POST_PURCHASE_STG,
} from '@/webapi/routes';
import { getDeviceBasedEditorId } from '@/webapi/common';
import { FeatureBit } from '@/webapi/use-auth-api';
import { CatalogCheckoutApp } from '@/webapi/checkout-widget-props';
import { openLinkInNewTab } from '@/utils/browser';
import { useCheckoutRecommend } from '@/features/editor/widgets/checkout-extensibility/loading/use-checkout-recommend';
import { Audience } from '@/features/dashboard/audiences/models';
import { useWatchLoadingLoop } from '@/features/editor/context/use-watch-loading-loop';
import { maybe } from '@/features/details/utils';
import { getTargetPlacement } from '@/features/editor/context/element-tracking-controls';
import { useDebounce } from 'react-use';
import { newMyExperiencesContext } from '@/features/dashboard/experiences/context';

export const EditorContext = React.createContext({} as EditorContextProps);

export function newEditorContext(
  resources: LoadedEditorResources,
): EditorContextProps {
  const {
    account: {
      store: { alias, domain, mainAlias },
    },
  } = useContext(AccountContext);
  const {
    running,
    draft,
    paused,
    loading: experiencesLoading,
  } = newMyExperiencesContext();

  const hasStartedLoadingRef = useRef(false);
  const [experiencesSettled, setExperiencesSettled] = useState(false);

  useEffect(() => {
    if (experiencesLoading) {
      hasStartedLoadingRef.current = true;
    } else if (hasStartedLoadingRef.current) {
      setExperiencesSettled(true);
    }
  }, [experiencesLoading]);

  const hasNoExperiences =
    experiencesSettled && draft === 0 && running === 0 && paused === 0;

  const [integrationType, setIntegrationType] = useState<'static' | 'spa'>(
    'static',
  );
  const [checkoutApps, setCheckoutApps] = useState([]);
  const [checkoutMountPoints, setCheckoutMountPoints] = useState<
    LightweightMountPoint[]
  >([]);
  const [checkoutSnapshotTimestamp, setCheckoutSnapshotTimestamp] = useState(0);
  const [currentExtension, setCurrentExtension] = useState<ExtensionType>(``);
  const [, setIsInErrorState, isInErrorStateRef] = useDetachedState(false);
  const loadedApps = useRef<Array<string>>([]);
  const [resetWidget, setResetWidget] = useState(false);

  const inspectorRef = useRef<HTMLElement>(null);
  const deviceRef = useRef<HTMLElement>(null);
  const { previewLoading, setPreviewLoading, previewLoadingRef } =
    usePreviewLoading();
  const [showElementTrackingPinkTip, setShowElementTrackingPinkTip] =
    useState(false);

  const [lastPickedElement, setLastPickedElement] = useDetachedState(
    {} as NewElementMessage,
  );

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const iframe = window.document.querySelector(
          '#navigator-iframe',
        ) as any;
        if (iframe?.contentWindow) {
          const vslyIntegrationType = iframe.contentWindow.vslyIntegrationType;
          if (vslyIntegrationType) {
            setIntegrationType(vslyIntegrationType);
            clearInterval(interval);
          }
        }
      } catch (e) {}
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const [newAudiences, setNewAudiences] = useState<Array<Audience>>([]);
  const isSpaIntegration = useFeatureBit(FeatureBit.SPA_INTEGRATION);
  const isThemesEnabled =
    useFeatureBit(FeatureBit.SHOPIFY_THEMES) && !isSpaIntegration;
  const isGoogleFontsEnabled = useFeatureBit(
    FeatureBit.SYNC_ALL_HEAD_GOOGLE_FONTS,
  );
  const experienceState = useExperienceState(
    expWithDefaultTheme(resources, isThemesEnabled),
    resources.appsCatalog,
  );
  const transpiler = useTranspiler();
  const inspectorNav = useInspectorNavigation(
    resources.appsCatalog,
    resources.experience.id,
  );

  const [hasEverHadChanges, setHasEverHadChanges] = useState(false);
  const changesCount =
    experienceState.getCurrentVariant()?.changes?.length ?? 0;

  useEffect(() => {
    if (!hasEverHadChanges && changesCount > 0) {
      setHasEverHadChanges(true);
    }
  }, [changesCount]);

  const storeUnsaved = hasNoExperiences && hasEverHadChanges;

  useCachedAutoSave(
    { ...experienceState.currentExperience, alias },
    experienceState.currentExperience.id,
    EDITOR_UNSAVED_EXPERIENCE_CACHE,
    storeUnsaved,
  );

  useCachedAutoSave(
    experienceState.currentExperience,
    experienceState.currentExperience.id,
    EDITOR_EXPERIENCE_CACHE,
  );

  useCheckoutRecommend(currentExtension, experienceState);

  const [lastChanges, setLastChanges] = useState(
    JSON.stringify(experienceState.getCurrentVariant()?.changes),
  );
  const { getChange } = useWidgetCatalogApi(false);

  const onAnchorMessageReceived = async (msg: ElementMessage) => {
    if (msg.widgetId && msg.widgetId !== ``) {
      const change = experienceState.findEditorChange(msg.loomiId);
      await inspectorNav.gotoCustomWidget(change, CustomWidgetStep.CUSTOMIZE);
    } else {
      const change = transpiler.asCompoundChange(
        EditorChangeKind.EDIT_COMPOUND,
        HtmlTransformKind.REPLACE,
        msg.selector,
        msg.selector,
        msg.html,
        msg.css,
        msg.js,
        compoundChangeId(
          msg.selector,
          experienceState?.currentExperience?.quickPreviewDevice,
        ),
      );
      inspectorNav.gotoCompoundEditor(change, true);
    }
  };

  function handleNavigateTo(msg: BaseMessage) {
    const casted = msg as NavigateToMessage;
    if (casted.url.startsWith(`/`)) {
      casted.url = `${domain}${casted.url}`;
    }
    deviceNavigation.navigateTo(casted.url);
  }

  async function handleVisualEdit(msg: BaseMessage) {
    const casted = msg as ElementMessage;

    if (casted.widgetId && !casted?.html?.includes('vslyai')) {
      const change = experienceState.findEditorChange(casted.loomiId);
      await inspectorNav.gotoCustomWidget(change, CustomWidgetStep.CUSTOMIZE);
    } else if (
      casted.loomiId &&
      currentExtension === `shopify-checkout-extensibility`
    ) {
      if (currentExtension === `shopify-checkout-extensibility`) {
        const change = experienceState.findEditorChange(casted.loomiId);
        inspectorNav.gotoCheckoutWidgetEdit(change);
      }
    } else if (
      casted.loomiId &&
      currentExtension === `` &&
      casted.html.includes(`vslyai`)
    ) {
      const change = experienceState.findEditorChange(casted.loomiId);
      inspectorNav.gotoAiWidgetBuilder(change);
    } else {
      const editorId = getDeviceBasedEditorId(
        casted.selector,
        devicePreview.editorState.device,
        experienceState.currentExperience.experienceCreationVersion,
      );
      const change = experienceState.findEditorChange(editorId);
      if (change?.block?.kind === `compound`) {
        inspectorNav.gotoLegacyVisualEditor(casted, change);
      } else {
        inspectorNav.gotoVisualEditor(
          casted.selector,
          casted.visualProps,
          change,
        );
      }
    }
  }

  function handleCodeEdit(msg: BaseMessage) {
    const casted = msg as ElementMessage;
    const change = transpiler.asCompoundChange(
      EditorChangeKind.EDIT_COMPOUND,
      HtmlTransformKind.REPLACE,
      casted.selector,
      casted.selector,
      casted.html,
      casted.css,
      casted.js,
      compoundChangeId(
        casted.selector,
        experienceState?.currentExperience?.quickPreviewDevice,
      ),
    );
    inspectorNav.gotoCompoundEditor(change, casted.isHtmlEditable);
  }

  function handleTrackElement(msg: BaseMessage) {
    const targetPlacement = getTargetPlacement(
      experienceState?.currentExperience,
    );
    const targetDevice = toTargetingDeviceType(
      experienceState?.lastPreviewDevice,
    );
    inspectorNav.gotoTrackElementEditor({
      type: `rule`,
      name: '',
      targetDevice,
      targetPlacement,
      rules: [
        {
          qbProps: {
            kind: 'LIST_EDITABLE',
            caption: 'action',
            envKey: 'action',
          },
          values: [{ op: 'click', value: (msg as ElementMessage).selector }],
        },
      ],
      description: '',
    } as ElementTracking);
  }

  function handleRemoveElement(msg: BaseMessage) {
    const casted = msg as ElementMessage;
    const existing = experienceState.findEditorChange(casted.loomiId);
    if (existing) {
      experienceState.removeEditorChange(existing);
    } else {
      if (currentExtension === `shopify-checkout-extensibility`) {
        const delChange = transpiler.asHideCheckoutWidgetChange(casted.loomiId);
        experienceState.upsertEditorChange(delChange);
      } else {
        const delChange = transpiler.asHideElementBlock(
          casted.selector,
          devicePreview.editorState.device,
        );
        experienceState.upsertEditorChange(delChange);
      }
    }
  }

  async function duplicateWidget(msg: ExternalEditMessage) {
    const change = await buildDuplicateWidgetChange(msg);
    inspectorNav.gotoCustomWidget(change, CustomWidgetStep.CUSTOMIZE).then();
  }

  async function buildDuplicateWidgetChange(msg: ExternalEditMessage) {
    const { change } = await getChange(msg?.experienceId, msg?.loomiId);
    change.editorId = `${msg?.loomiId}_dup`;
    const value = change.block.value as WidgetChange;
    value.env[`sectionId`] = `#${change.editorId}`;
    value.env[ORIGINAL_EXP_NAME] = msg.experienceName;
    value.env[ORIGINAL_EXP_ID] = msg.experienceId;
    change.editorSelector = `#${change.editorId}`;
    value.htmlKind = `replace`;
    change.block.kind = `widget`;
    change.block.selector = `#${msg?.loomiId}`;
    return change;
  }

  async function copyElement(msg: CopyElementMessage) {
    let change: EditorDeclarativeBlock;
    if (!!msg.widgetId && !!msg.experienceId) {
      change = await buildDuplicateWidgetChange({
        action: `copyElement`,
        experienceId: msg.experienceId,
        experienceName: msg.experienceName,
        widgetId: msg.widgetId,
        loomiId: msg.loomiId,
      });
    } else if (!!msg.widgetId && !msg.experienceId) {
      change = experienceState.findEditorChange(msg.loomiId);
    } else {
      change = transpiler.asCompoundChange(
        EditorChangeKind.NEW_COMPONENT,
        HtmlTransformKind.APPEND_AFTER,
        msg.selector,
        msg.selector,
        msg.html,
      );
    }

    const newChange = produce(change, (draft) => {
      const id = `_loomi_addon_${new Date().getTime()}`;
      draft.editorId = id;
      draft.editorSelector = `#${id}`;
      draft.block.value[`htmlKind`] = `appendAfter`;
      draft.block.selector = `#vsly-invalid-selector`;
      if (draft.block.value?.[`env`]) {
        draft.block.value[`env`][`sectionId`] = draft.editorSelector;
      }
    });

    inspectorNav.gotoChangeSelector(newChange, true);
  }

  async function moveElement(msg: MoveElementMessage) {
    const existingLoomiChange = msg.loomiId
      ? experienceState.findEditorChange(msg.loomiId)
      : undefined;

    if ((!!msg.widgetId || !!msg.loomiId) && existingLoomiChange) {
      inspectorNav.gotoChangeSelector(existingLoomiChange, false);
    } else if (msg.origMoveSelector) {
      const editorId = getDeviceBasedEditorId(
        `move-${msg.origMoveSelector}`,
        devicePreview.editorState.device,
        experienceState.currentExperience.experienceCreationVersion,
      );
      const change =
        experienceState.findEditorChange(editorId) ||
        experienceState
          .getCurrentVariant()
          ?.changes?.find(
            (ch) =>
              ch.block?.kind === `moveElem` &&
              ch.block?.selector === msg.origMoveSelector,
          );
      if (change) {
        inspectorNav.gotoMoveElement(change, false);
      } else {
        inspectorNav.gotoMoveElement(
          transpiler.asMoveChange(
            editorId,
            msg.origMoveSelector,
            msg.origMoveSelector,
            `appendBefore`,
          ),
          true,
        );
      }
    } else {
      const change = transpiler.asMoveChange(
        getDeviceBasedEditorId(
          `move-${msg.selector}`,
          devicePreview.editorState.device,
          experienceState.currentExperience.experienceCreationVersion,
        ),
        msg.selector,
        msg.selector,
        `appendBefore`,
      );
      inspectorNav.gotoMoveElement(change, true);
    }
  }

  const handleLoadedMessage = (msg: LoadedMessage) => {
    const linkedFonts = isGoogleFontsEnabled
      ? extractGoogleFontsFromIframe()
      : [];
    const mergedFonts = Array.from(
      new Set([...linkedFonts, ...(msg.fonts || [])]),
    );

    if (mergedFonts.length > 0) {
      devicePreview.setInstalledFonts(mergedFonts, msg.mostPopularFont);
    }
    if (msg.currentLocale) {
      devicePreview.setPreviewLocale(msg.currentLocale);
    }
    setPreviewLoading(false);

    if (msg.extensionName === `shopify-post-purchase`) {
      setCurrentExtension(`shopify-post-purchase`);
      window.dispatchEvent(new CustomEvent(`post-purchase-reload`));
      inspectorNav.gotoPostPurchaseEditor();
    } else if (msg.extensionName === `shopify-checkout-extensibility`) {
      setCurrentExtension(`shopify-checkout-extensibility`);
      if (msg.catalog) {
        msg?.catalog.forEach((app) => {
          app?.checkoutWidgets?.forEach((widget) => {
            if (
              !!widget?.initialState?.['loadingEnv']?.[`recommendationOptions`]
            ) {
              widget.initialState['loadingEnv'][`recommendationOptions`][
                `storeAlias`
              ] = alias;
            }
          });
        });
        setCheckoutApps(msg.catalog);
      }
      handleCheckoutLoadedMessage(msg);
    } else {
      if (currentExtension === `shopify-checkout-extensibility`) {
        setTimeout(() => {
          inspectorNav.gotoChangelog();
        }, 500);
      } else {
        const redirectChange = experienceState.getPageRedirectChange();
        if (redirectChange) {
          setTimeout(() => {
            inspectorNav.gotoPageRedirectWidget(redirectChange);
          }, 500);
        }

        const themeChange = experienceState.getThemeChangeChange();
        if (themeChange) {
          setTimeout(() => {
            inspectorNav.gotoThemeTest(themeChange);
          }, 500);
        }

        const priceTestChange = experienceState.getPriceTestChange();
        if (priceTestChange) {
          setTimeout(() => {
            inspectorNav.gotoPriceTest(priceTestChange);
          }, 500);
        }
      }

      setCurrentExtension(``);
    }
  };

  function handleCheckoutLoadedMessage(msg: LoadedMessage) {
    setCheckoutMountPoints(msg?.mountPoints || []);
    setCheckoutSnapshotTimestamp(msg?.snapshotTimestamp || 0);
    const count = msg.mountPointsCount || 0;
    if (count == 0) {
      inspectorNav.gotoCheckoutNoMountPoints();
    } else {
      inspectorNav.gotoChangelog();
    }
  }

  function handleLoadingError(msg: PageLoadErrorMessage) {
    const placementKind =
      experienceState.currentExperience.targeting.placement.kind;
    const redirectionUrl = (
      msg?.redirectionUrl ||
      msg?.originalUrl ||
      ``
    ).replace(domain, ``);

    const isInAllProducts = () =>
      placementKind === PlacementKind.ALL_PRODUCTS &&
      redirectionUrl?.includes(`/products/`);

    const isInAllCollections = () =>
      placementKind === PlacementKind.ALL_CATEGORIES &&
      redirectionUrl.includes(`/collections/`);

    if (
      msg.type === `REDIRECTION` &&
      (isInAllProducts() || isInAllCollections())
    ) {
      let navUrl = redirectionUrl.startsWith(`https://`)
        ? redirectionUrl
        : `${domain}${redirectionUrl}`;
      navUrl = navUrl.replace(`?init-req=true`, ``);
      deviceNavigation.navigateTo(navUrl);
    } else if (msg.type === `ERROR` && isInAllProducts()) {
      const page = deviceNavigation
        .getRelatedUrls()[0]
        .filter((_) => Math.round(Math.random() * 100) % 3 !== 0)?.[0];
      deviceNavigation.navigateTo(page.url);
    } else if (msg.type === `ERROR` && isInAllCollections()) {
      const page = deviceNavigation
        .getRelatedUrls()[0]
        .filter((_) => Math.round(Math.random() * 100) % 3 !== 0)?.[0];
      deviceNavigation.navigateTo(page.url);
    } else {
      setIsInErrorState(true);
    }
  }

  function handleOpenMiniCart() {
    const placementKind = maybe(
      () => experienceState.currentExperience.targeting.placement.kind,
    );
    const previewUrl = maybe(() => deviceNavigation.previewUrl);
    if (
      placementKind === PlacementKind.MINI_CART ||
      (typeof previewUrl === `string` && previewUrl.includes(MINI_CART_TAG))
    ) {
      devicePreview.sendJsCode('window.loomi_api?.openDrawer()');
      return;
    }
  }

  function handleCheckoutNewComponent(msg: CheckoutNewComponent) {
    inspectorNav.gotoCheckoutAppListing(msg);
  }

  function onAiVisualEdit(msg: AiVisualEditMessage) {
    window.dispatchEvent(new CustomEvent(`ai-visual-edit`, { detail: msg }));
  }

  function onAiSectionEdit(msg: ElementMessage) {
    const mobileBasedEditorId = `ai-edit-${getDeviceBasedEditorId(
      msg.selector,
      DeviceType.Mobile,
      experienceState.currentExperience.experienceCreationVersion,
    )}`;
    const desktopBasedEditorId = `ai-edit-${getDeviceBasedEditorId(
      msg.selector,
      DeviceType.Desktop,
      experienceState.currentExperience.experienceCreationVersion,
    )}`;

    const isMobile = devicePreview.editorState.device === DeviceType.Mobile;

    const primaryEditorId = isMobile
      ? mobileBasedEditorId
      : desktopBasedEditorId;
    const fallbackEditorId = isMobile
      ? desktopBasedEditorId
      : mobileBasedEditorId;

    msg.editorId = primaryEditorId;
    let change = experienceState.findEditorChange(primaryEditorId);
    if (change) {
      inspectorNav.gotoAiSectionEditor(msg, change);
      return;
    } else {
      change = experienceState.findEditorChange(fallbackEditorId);
      if (change?.targetDevice === TargetingDeviceType.ALL_DEVICES) {
        inspectorNav.gotoAiSectionEditor(msg, change);
        return;
      }
    }

    inspectorNav.gotoAiSectionEditor(msg, undefined);
  }

  function onSectionModifierAdd(msg: SectionModifierAddMessage) {
    window.dispatchEvent(new CustomEvent(`aiSectionAdd`, { detail: msg }));
  }
  // @ts-ignore
  window.setPreviewLoading = setPreviewLoading;

  const onInboundMessage = async (msg: BaseMessage) => {
    switch (msg.action) {
      case `anchor`:
        await onAnchorMessageReceived(msg as ElementMessage);
        break;
      case `create`:
        inspectorNav.gotoAppCatalog(msg as NewElementMessage);
        break;
      case `exit`:
        inspectorNav.gotoChangelog();
        break;
      case `loaded`:
        handleLoadedMessage(msg as LoadedMessage);
        handleOpenMiniCart();
        break;
      case `loading`:
        setPreviewLoading(true);
        devicePreview.sendInitMessage();
        break;
      case `load_error`:
        setPreviewLoading(false);
        handleLoadingError(msg as PageLoadErrorMessage);
        break;
      case `pick`:
        setLastPickedElement(msg as NewElementMessage);
        break;
      case `navigateTo`:
        handleNavigateTo(msg);
        break;
      case `visualEdit`:
        await handleVisualEdit(msg);
        break;
      case `codeEdit`:
        handleCodeEdit(msg);
        break;
      case `trackElement`:
        handleTrackElement(msg);
        break;
      case `removeElement`:
        handleRemoveElement(msg);
        break;
      case `editExternal`:
        duplicateWidget(msg as ExternalEditMessage);
        break;
      case `copyElement`:
        copyElement(msg as CopyElementMessage);
        break;
      case `moveElement`:
        moveElement(msg as MoveElementMessage);
        break;
      case `checkoutNewComponent`:
        handleCheckoutNewComponent(msg as CheckoutNewComponent);
        break;
      case `openLink`:
        openLinkInNewTab(
          (msg as OpenLinkMessage).url,
          (msg as OpenLinkMessage).tabName,
        );
        break;
      case `aiVisualEditAction`:
        onAiVisualEdit(msg as AiVisualEditMessage);
        break;
      case `aiSectionEdit`:
        onAiSectionEdit(msg as ElementMessage);
        break;
      case `section_modifier_added`:
        onSectionModifierAdd(msg as SectionModifierAddMessage);
        break;
      case `cartActionDone`:
        window.dispatchEvent(new Event('vsly-cart-action-done'));
        break;
      default:
        console.warn(
          `vsly-editor`,
          `no handler for message: ${msg.action}`,
          msg,
        );
    }
  };

  const devicePreview = useDevicePreview(
    onInboundMessage,
    experienceState.latestChangeEdit,
    experienceState.setQuickPreviewInfo,
    mainAlias || alias,
    resources.experience.id,
    experienceState?.currentExperience?.targeting?.other,
  );
  useKeepPreviewDevice(experienceState, devicePreview);

  const experienceControls = useExperienceControls(
    experienceState,
    inspectorNav,
    devicePreview?.editorState?.device,
  );

  const deviceNavigation = useDeviceNavigation(
    resources.collections,
    resources.products,
    experienceState.currentExperience,
    experienceState.setQuickPreviewInfo,
    setPreviewLoading,
    loadedApps,
    devicePreview?.editorState?.device,
  );

  const reloadQuickPreviewInfo = () => {
    if (deviceNavigation && deviceNavigation.reloadQuickPreviewInfo) {
      deviceNavigation.reloadQuickPreviewInfo();
    }
  };

  useWatchLoadingLoop(
    previewLoadingRef,
    devicePreview,
    deviceNavigation,
    handleLoadedMessage,
  );

  useEffect(() => {
    const currentChanges = JSON.stringify(
      experienceState?.getCurrentVariant()?.changes,
    );
    if (previewLoading === false && lastChanges !== currentChanges) {
      setLastChanges(currentChanges);
      resyncMutations();
    }
  }, [previewLoading, experienceState?.getCurrentVariant()?.changes]);

  useDebounce(
    () => {
      if (!previewLoading) {
        resyncMutations();
      }
    },
    100,
    [
      previewLoading,
      experienceState.currentExperience.quickPreviewPlacement,
      experienceState.currentExperience.quickPreviewDevice,
    ],
  );

  const getTargetedChanges = () =>
    experienceState
      .getCurrentVariant()
      .changes?.filter((ch) =>
        shouldSync(experienceState.currentExperience, ch),
      );

  const revertTempChange = () => {
    setResetWidget(!resetWidget);
  };

  useEffect(() => {
    if (!previewLoading) {
      setIsInErrorState(false);
    }
  }, [previewLoading]);

  const applyTempChange = useDebouncedCallback(
    (change: EditorDeclarativeBlock) => {
      let mutationsCopy: EditorDeclarativeBlock[];

      if (experienceState.getCurrentVariant()?.changes?.length > 0) {
        const targetedChanges = getTargetedChanges() || [];
        mutationsCopy = JSON.parse(JSON.stringify(targetedChanges));
      } else {
        mutationsCopy = [];
      }

      const tempChange = JSON.parse(
        JSON.stringify(change),
      ) as EditorDeclarativeBlock;

      const idx = mutationsCopy.findIndex(
        (m) => m.editorId === tempChange.editorId,
      );
      if (idx > -1) {
        tempChange.block.order = mutationsCopy[idx].block.order;
        mutationsCopy[idx] = tempChange;
      } else {
        mutationsCopy.push(tempChange);
      }
      devicePreview.syncChanges(transpiler.asDeclarativeBlock(mutationsCopy));
    },
    100,
  );

  const resyncMutations = () => {
    reloadQuickPreviewInfo();
    const mutations = transpiler.asDeclarativeBlock(getTargetedChanges());
    devicePreview.syncChanges(mutations);
  };

  const isEditingCheckoutExtensibility = useMemo(() => {
    return (
      currentExtension === `shopify-checkout-extensibility` ||
      deviceNavigation.previewUrl.includes(`shopify_checkout_extensibility`)
    );
  }, [deviceNavigation.previewUrl, currentExtension]);

  useEffect(() => {
    if (resources.experience.isPostPurchase) {
      const isProd = routes.getEnvEnum() === Env.PRODUCTION;
      deviceNavigation.navigateTo(
        isProd ? SHOPIFY_POST_PURCHASE_PROD : SHOPIFY_POST_PURCHASE_STG,
      );
    }
  }, []);

  function extractGoogleFontsFromIframe(): string[] {
    const linked: string[] = [];
    devicePreview.runInIframe((doc) => {
      const links = Array.from(
        doc.querySelectorAll<HTMLLinkElement>(`link[rel="stylesheet"]`),
      );
      for (const link of links) {
        if (!link.href?.includes(`fonts.googleapis.com`)) continue;
        try {
          const url = new URL(link.href);
          url.searchParams.getAll(`family`).forEach((familyParam) => {
            const name = familyParam.split(`:`)[0].replace(/\+/g, ` `).trim();
            if (name) linked.push(name);
          });
        } catch (_) {
          // malformed URL — skip
        }
      }
    });
    return linked;
  }

  return {
    transpiler,
    experienceState,
    inspectorNav,
    devicePreview,
    deviceNavigation,
    experienceControls,
    revertTempChange,
    applyTempChange,
    previewLoading,
    lastPickedElement,
    resyncMutations,
    resources,
    resetWidget,
    inspectorRef,
    deviceRef,
    newAudiences,
    setNewAudiences,
    cleanLastPickedElement: () => setLastPickedElement({} as NewElementMessage),
    loadedApps,
    isInErrorStateRef,
    currentExtension,
    isEditingCheckoutExtensibility,
    checkoutApps,
    checkoutMountPoints,
    integrationType,
    checkoutSnapshotTimestamp,
    showElementTrackingPinkTip,
    setShowElementTrackingPinkTip,
  };
}

export interface EditorContextProps {
  resetWidget: boolean;
  cleanLastPickedElement: () => void;
  newAudiences: Array<Audience>;
  setNewAudiences: (a: Array<Audience>) => void;
  revertTempChange: () => void;
  transpiler: TranspilerHook;
  inspectorNav: InspectorNavigationHook;
  devicePreview: DevicePreviewHook;
  deviceNavigation: UseDeviceNavigationHook;
  experienceControls: UseExperienceControls;
  experienceState: ExperienceStateHook;
  applyTempChange: (change: EditorDeclarativeBlock) => void;
  previewLoading: boolean;
  lastPickedElement: NewElementMessage;
  resyncMutations: () => void;
  resources: LoadedEditorResources;
  inspectorRef: React.MutableRefObject<HTMLElement>;
  deviceRef: React.MutableRefObject<HTMLElement>;
  loadedApps: React.MutableRefObject<Array<string>>;
  isInErrorStateRef: React.MutableRefObject<boolean>;
  currentExtension: ExtensionType;
  isEditingCheckoutExtensibility: boolean;
  checkoutApps: CatalogCheckoutApp[];
  checkoutMountPoints: LightweightMountPoint[];
  integrationType: 'static' | 'spa';
  checkoutSnapshotTimestamp: number;
  showElementTrackingPinkTip: boolean;
  setShowElementTrackingPinkTip: React.Dispatch<React.SetStateAction<boolean>>;
}

export function compoundChangeId(
  selector: string,
  quickPreviewDevice: DeviceType,
) {
  return `${selector}-${quickPreviewDevice?.toString()?.toLowerCase()}`;
}

function useKeepPreviewDevice(
  experienceState: ExperienceStateHook,
  devicePreview: DevicePreviewHook,
) {
  useEffect(() => {
    experienceState.setLastPreviewDevice(devicePreview?.editorState?.device);
  }, [devicePreview?.editorState?.device]);

  useEffect(() => {
    const initialDevice = resolveInitialDevice(
      experienceState.currentExperience,
    );
    if (initialDevice) {
      devicePreview.setPreviewDevice(initialDevice);
    }
  }, []);
}

function resolveInitialDevice(experience: Experience): DeviceType | null {
  if (hasOnlyMobileChanges(experience)) {
    return DeviceType.Mobile;
  }
  return experience?.lastDevicePreview || null;
}

function hasOnlyMobileChanges(experience: Experience): boolean {
  const { variants, targeting } = experience || {};
  const isMobileTargeting =
    targeting?.device === TargetingDeviceType.ALL_DEVICES ||
    targeting?.device === TargetingDeviceType.MOBILE ||
    targeting?.device === undefined;

  if (!isMobileTargeting || !variants?.length) {
    return false;
  }

  const allChanges = variants.flatMap((v) => v.changes || []);
  return (
    allChanges.length > 0 &&
    allChanges.every((c) => c.targetDevice === TargetingDeviceType.MOBILE)
  );
}

function usePreviewLoading() {
  const [previewLoading, setPreviewLoading, previewLoadingRef] =
    useDetachedState(true);
  const timeoutRef = useRef(null);
  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(
      () => !previewLoading && setPreviewLoading(false),
      1000 * 30,
    );
    return () => clearTimeout(timeoutRef.current);
  }, [previewLoading]);
  return { previewLoading, setPreviewLoading, previewLoadingRef };
}

function expWithDefaultTheme(
  resources: LoadedEditorResources,
  isThemesEnabled: boolean,
) {
  if (!isThemesEnabled) {
    return { ...resources.experience, themeId: undefined };
  }
  const mainThemeId = resources?.themes?.find((t) => t.role === `main`)?.id;
  try {
    let themeId =
      resources.themes?.find((t) => t.id == resources?.experience?.themeId)
        ?.id || 0;
    if (themeId === mainThemeId) {
      themeId = 0;
    }
    return {
      ...resources.experience,
      themeId,
      mainThemeId,
    };
  } catch (e) {
    return { ...resources.experience, themeId: 0, mainThemeId };
  }
}
